/**
 * My Organization API + hook (own-org scoped, read-only).
 *
 * Wraps GET /api/v1/my-organization (see server/routes/
 * non_generated_single_table_business_logic_routes/my-organization-business-logic.ts).
 *
 * Why this exists instead of the generated useOrganizations(id) hook: that hook
 * hits GET /api/v1/organizations/:id, which gates on `users:manage` (admin only),
 * so members saw no organization name and no default delivery address. Use this
 * for display-only reads of the current user's org; admins editing the org still
 * use the generated hooks (and must invalidate MY_ORGANIZATION_QUERY_KEY after a
 * save so these displays refresh).
 */

import { useQuery } from '@tanstack/react-query';

import { useAppSelector } from '../lib/reduxHooks.js';
import { selectUser } from '../store/slices/auth.js';

import { httpClient } from './http-client.js';

export interface MyOrganization {
  id: string;
  name: string;
  default_delivery_address: string | null;
}

export const MY_ORGANIZATION_QUERY_KEY = ['my-organization'] as const;

/** The current user's own organization (null when they have none). */
export function useMyOrganization() {
  const userId = useAppSelector(selectUser)?.id;
  return useQuery({
    // Keyed by user so a different login in the same browser never reuses it.
    queryKey: [...MY_ORGANIZATION_QUERY_KEY, userId],
    queryFn: async () => {
      const res = await httpClient.get<{ data: MyOrganization | null; success: boolean }>(
        '/my-organization',
      );
      return res.data;
    },
    enabled: !!userId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
